import { debounce } from 'lodash-es';
import ajax from '@/utils/ajax';
import { MemberStruct, InfoStruct } from '@/types/meeting';
import '@/services/index';
import { baseConvert, convertMeetingInfo, convertMemberInfo } from '@/api/model/toV3';
import { distributePromise } from '@/utils/distributePromise';

export interface LoginRequest {
  authType: string;
  guestAvatarId: number;
  guestName: string;
  meetingId: number;
  pwd: string;
}

export enum DEVICE_STATUS_REQUEST {
  OPEN = 1,
  CLOSE = 2,
}
export enum DEVICE_TYPE {
  VIDEO = 2,
  AUDIO = 1,
}

export enum SPACE_STATUS {
  OPEN = 1,
  SPACE_ON = 2,
}

export type deviceVersionMap = {
  meetingId: number;
  browser: string;
  browserVersion: string;
  os: string;
  osVersion: string;
};

export const meetingId = {
  value: '',
  toString() {
    if (!this.value) {
      this.value = window.location.pathname.match(/\/meet\/(\d{6,9})/)?.[1] || '';
    }
    return this.value;
  },
};

const GLOBAL = {
  channelId: '',
};

// 设置channelId
export function setChannelId(channelId: string) {
  GLOBAL.channelId = channelId;
}

// 批量获取，单个分发获取user
export const getUser = distributePromise(
  (yxUids: string[]) => {
    if (!GLOBAL.channelId)
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          reject('channelId没有获取');
        }, 1000);
      });
    return API.api.webMeetingV3.queryMembers
      .request({
        meetingId: meetingId.toString(),
        channelId: GLOBAL.channelId,
        yxUid: yxUids.join(','),
      })
      .then((res) => {
        if (res.data?.length) {
          const ret: Record<string | number, defs.api.ParticipantsInformationV3> = {};
          res.data.forEach((n) => {
            ret[n.yxUid!] = n;
          });
          return ret;
        }
        throw new Error(res.message);
      });
  },
  { wait: 2 }
);

/**
 * 游客加入会议
 * @param data
 * @returns
 */
export const login = (data: LoginRequest) => {
  return API.api.webMeetingAuthV3.login.request({ meetingId: data.meetingId.toString() }, data);
};

// 面试官加入会议
export const openIdLogin = (data: LoginRequest) => {
  return API.api.webMeetingOpenAuthV3.openIdLogin.request(
    { meetingId: data.meetingId.toString() },
    data
  );
};

// 退出会议
export const logout = (meetingId: string) => {
  return API.api.webMeetingAuthV3.logout.request({ meetingId });
};

// 获取会议信息
export const getMeetingInfo = (
  meetingId: string
): Promise<defs.api.TheJSONResponseObjectWrapperClasses<InfoStruct>> => {
  return API.api.webMeetingV3.getMeetingInfo
    .request({ meetingId })
    .then((n) => baseConvert(n, convertMeetingInfo));
};

// 加入会议并获取信息
// web调用 check-in 接口的时候，如果返回了 510002 错误码，需要再调用一次 check-in；如果第二次还是返回了 510002 错误码，就不需要再调用了，可以提示给用户会议已结束。
// 原因是这样的：云信SDK在web刷新的时候，SDK会先退出会议房间，然后云信服务器会销毁会议房间；等到刷新后check-in的时候，popo服务端可能还没收到云信的房间销毁通知，使用上一个会议房间的channelId去初始化资源，这就可能导致第一次check-in可能返回 510002 错误码。所以需要第二次重试，这时有了一个时间差，基本上是能够收到云信服务器的通知了（如果长时间没收到的话肯定服务端出问题）。
export const meetingCheckIn = async (params: {
  meetingId?: string;
  scheduleSt?: string;
  firstMemberNotifyOn?: string;
  password?: string;
}): Promise<defs.api.TheJSONResponseObjectWrapperClasses<InfoStruct>> => {
  let res;
  try {
    res = await API.api.webMeetingV3.checkIn.request(
      { meetingId: params.meetingId },
      { ...params }
    );
  } catch (e) {
    console.warn('第一次checkIn失败', e);
  }
  if (res?.status !== 1) {
    res = await API.api.webMeetingV3.checkIn.request(
      { meetingId: params.meetingId },
      { ...params }
    );
  }
  return baseConvert(res, convertMeetingInfo);
};

export function getAvailableApps(meetingId: string, cid: number) {
  return API.api.webMeetingV3.getAvailableApps.request({ meetingId, channelId: cid });
}

// 上报设备状态
export const reportDeviceStatus = (
  cid: string,
  audioOn: DEVICE_STATUS_REQUEST,
  videoOn: DEVICE_STATUS_REQUEST
) => {
  return API.api.webMeetingV3.submitPeripheralStatus.request(
    {
      meetingId: meetingId.toString(),
    },
    {
      channelId: +cid,
      status: [
        {
          peripheralStatus: audioOn,
          peripheralType: 1,
        },
        {
          peripheralStatus: videoOn,
          peripheralType: 2,
        },
      ],
    }
  );
};
// 更新设备状态
export const updateDeviceStatus = (cid: string, type: DEVICE_TYPE, on: DEVICE_STATUS_REQUEST) => {
  return API.api.webMeetingV3.submitPeripheralStatus.request(
    {
      meetingId: meetingId.toString(),
    },
    {
      channelId: +cid,
      status: [
        {
          peripheralStatus: on,
          peripheralType: type,
        },
      ],
    }
  );
};
// 静音 / 取消静音
export const quiet = (cid: string, on: DEVICE_STATUS_REQUEST, mode: SPACE_STATUS) => {
  return API.api.webMikeControlV3.controlSelfMike.request(
    {},
    { channelId: +cid, mikeStatus: on, mode }
  );
};

// 取消静音
export const openVoice = () => {};

// 获取评价问题
export const getFeedbackQuestion = (meetingId: string, locale: string, docType = 2) => {
  return API.api.webFeedbackV3.doc.request({ meetingId, language: locale, type: docType });
};

// 获取会议录制状态
export function getRecordingStatus(channelId: number) {
  return API.api.webRecordingV3.getRecordingStatus.request({ channelId });
}

// 申请开启转录
export function requestRecording(channelId: number) {
  return API.api.webRecordingV3.requestRecording.request({}, { channelId });
}

// 离开会议
export const hangUp = (
  meetingId: string,
  cid: number,
  uid: string,
  isGreat: boolean,
  feedbackIds?: number[],
  remark?: string
) => {
  return API.api.webFeedbackV3.leave.request(
    {
      meetingId: meetingId.toString(),
    },
    {
      channelId: cid,
      feedbackDocIds: feedbackIds,
      feedbackType: 2,
      isGreat,
      meetingId: +meetingId,
      remark,
      uid,
    }
  );
};

// 根据云信 uid 获取用户信息
export const queryMemberInfoByYXUid = (
  cid: number,
  yxUids: string[] | number[]
): Promise<defs.api.TheJSONResponseObjectWrapperClasses<MemberStruct[]>> => {
  return API.api.webMeetingV3.queryMembers
    .request({
      meetingId: meetingId.toString(),
      channelId: cid.toString(),
      yxUid: yxUids.join(','),
    })
    .then((res) => baseConvert(res, (data) => data.map(convertMemberInfo)));
};

const promiseMap = {};
let waitYxUids: number[] = [];
const maxLength = 100;
const queryMemberInfoByYXUidMerge = debounce((cid: number) => {
  waitYxUids = [...new Set(waitYxUids)];
  const yxUids = waitYxUids.slice(0, maxLength - 1);
  waitYxUids = waitYxUids.slice(maxLength);
  return API.api.webMeetingV3.queryMembers
    .request({
      meetingId: meetingId.toString(),
      channelId: cid.toString(),
      yxUid: yxUids.join(','),
    })
    .then((res) => baseConvert(res, (data) => data.map(convertMemberInfo)))
    .then((res) => {
      const { status, message } = res;
      if (res.status === 1) {
        res.data.forEach((n) => {
          const promise = promiseMap[n.yxUid];
          if (promise) {
            promise.resolve({
              status,
              message,
              data: n,
            });
            delete promiseMap[n];
          }
        });
      } else {
        yxUids.forEach((n) => {
          const promise = promiseMap[n];
          if (promise) {
            promise.resolve({
              status,
              message,
            });
            delete promiseMap[n];
          }
        });
      }
    })
    .catch((err) => {
      yxUids.forEach((n) => {
        const promise = promiseMap[n.toString()];
        if (promise) {
          promise.reject(err);
          delete promiseMap[n.toString()];
        }
      });
    })
    .finally(() => {
      if (waitYxUids.length) {
        queryMemberInfoByYXUidMerge(cid);
      }
    });
}, 100);
// 获取单个文件信息，合并获取
export const queryMemberByYXUidOne = (
  cid: number,
  yxUid: number
): Promise<defs.api.TheJSONResponseObjectWrapperClasses<MemberStruct | undefined>> => {
  return new Promise<defs.api.TheJSONResponseObjectWrapperClasses<MemberStruct | undefined>>(
    (resolve, reject) => {
      promiseMap[yxUid.toString()] = { resolve, reject };
      waitYxUids.push(yxUid);
      queryMemberInfoByYXUidMerge(cid);
    }
  );
};

/**
 * 获取头像列表
 * @returns
 */
export const getAvatarList = () => {
  return API.api.webAvatarV3.listAvatar.request({});
};
/**
 * 获取当前会话信息
 * @param meetingId
 * @returns
 */
export const getUserInfo = (
  meetingId: string
): Promise<defs.api.TheJSONResponseObjectWrapperClasses<defs.api.TheWebClientSession>> => {
  return API.api.webMeetingAuthV3.getSession.request({ meetingId });
};

/**
 * 获取 OpenID 鉴权地址接口
 * @param meetingId
 * @param pwd
 * @returns
 */
export const getOpenIDUrl = (
  meetingId: string,
  pwd: string,
  provider?: string
): Promise<API.api.webMeetingOpenAuthV3.openIdAuthURL.Response> => {
  return API.api.webMeetingOpenAuthV3.openIdAuthURL.request({
    meetingId,
    pwd,
    provider,
  });
};

// 获取当前用户信息
export const getChatInfo = (
  meetingId: string,
  cid: string
): Promise<defs.api.TheJSONResponseObjectWrapperClasses<defs.api.MeetingRoomsInformation>> => {
  return API.api.webMeetingV3.getChatRoomLink.request({
    channelId: cid,
    meetingId,
  });
};

export const screenShare = (
  cid: string | number,
  state: boolean
): Promise<
  defs.api.TheJSONResponseObjectWrapperClasses<defs.api.TheInteractiveWhiteboardCloudLetterParameters>
> => {
  // return ajax.get(`/api/bs-vmeeting/v1/member/screen/share`, {
  //   params: {
  //     cid: cid.toString(),
  //     state: Number(state),
  //   },
  // });
  return API.api.webScreenShareV3.modifyScreenShareStatus.request(
    {
      meetingId: meetingId.toString(),
    },
    {
      channelId: +cid,
      shareStatus: Number(state) + 1,
    }
  );
};

export const schemaEncrypt = (business: string, param: any) => {
  return ajax.post(`/open-apis/no-auth/meeting/v1/schema/encrypt`, {
    data: {
      business,
      business_param: param,
    },
  });
};

/**
 * 查询白板认证参数
 * @param channelId
 * @returns
 */
export const whiteboardAuth = (
  channelId: number
): Promise<
  defs.api.TheJSONResponseObjectWrapperClasses<defs.api.TheInteractiveWhiteboardCloudLetterParameters>
> => {
  return API.api.webWhiteboardV3.getYXAuth.request({ channelId });
};

/**
 * 启用白板
 * @param channelId
 * @returns
 */
export const enableWhiteboard = (
  channelId: number
): Promise<
  defs.api.TheJSONResponseObjectWrapperClasses<defs.api.TheInteractiveWhiteboardCloudLetterParameters>
> => {
  return API.api.webWhiteboardV3.enableWhiteboard.request({}, { channelId });
};

/**
 * 停用白板
 * @param channelId
 * @returns
 */
export const disableWhiteboard = (
  channelId: number
): Promise<defs.api.TheJSONResponseObjectWrapperClasses> => {
  return API.api.webWhiteboardV3.disableWhiteboard.request({}, { channelId });
};

/**
 * 保存互动白板快照
 * @param data
 * @returns
 */
export const uploadWhiteboardSnapshot = async (data: {
  channelId: number;
  fileBase64: string;
  fileNameExtension: string;
}): Promise<defs.api.TheJSONResponseObjectWrapperClasses> => {
  return ajax.post('/api/bs-vmeeting/v3/meeting/whiteboard/snapshot/upload', { data });
};

/**
 * 开启代码编辑器
 * @param channelId
 * @returns
 */
export const enableCodeEditor = (
  channelId: number
): Promise<defs.api.TheJSONResponseObjectWrapperClasses> => {
  // return new Promise(resolve => setTimeout(resolve));
  return API.api.webCodeEditorV3.enableCodeEditor.request({}, { channelId });
};

/**
 * 关闭代码编辑器
 * @param channelId
 * @returns
 */
export const disableCodeEditor = (
  channelId: number
): Promise<defs.api.TheJSONResponseObjectWrapperClasses> => {
  return API.api.webCodeEditorV3.disableCodeEditor.request({}, { channelId });
};

/**
 * 获取同声传译的状态和议员配置信息
 * @param params
 * @returns
 */
export const getInterpretStatus = (params: { channelId: number }) => {
  return API.api.webInterpretChannelV3.getInterpretStatus.request(params, 0);
};

/**
 * 获取频道列表 不包含主频道
 * @param params
 * @returns
 */
export const getInterpretChannels = (
  params: API.api.webInterpretChannelV3.getInterpretChannels.Params
) => {
  return API.api.webInterpretChannelV3.getInterpretChannels.request(params);
};

/**
 * 参会人订阅语言频道
 * @param params
 * @returns
 */
export const subscribe = (params: API.api.webInterpretChannelV3.subscribe.Params) => {
  return API.api.webInterpretChannelV3.subscribe.request({}, params);
};
